import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauth, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (error) {
      // Expected on first load when unauthenticated — no need to surface.
      if (error?.response?.status && error.response.status !== 401) {
        console.error("Auth check error:", error);
      }
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // 2-step: backend issues OTP challenge instead of JWT
      if (data?.step === "otp_required") {
        return {
          ok: true,
          otpRequired: true,
          challengeId: data.challenge_id,
          emailMasked: data.email_masked,
          expiresAt: data.expires_at,
          resendAfter: data.resend_after_seconds || 30,
        };
      }
      if (data.token) localStorage.setItem("wf_token", data.token);
      setUser(data);
      return { ok: true, user: data };
    } catch (error) {
      return { ok: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, []);

  const verifyOtp = useCallback(async (challengeId, code) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { challenge_id: challengeId, code });
      if (data.token) localStorage.setItem("wf_token", data.token);
      setUser(data);
      return { ok: true, user: data };
    } catch (error) {
      return { ok: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, []);

  const resendOtp = useCallback(async (challengeId) => {
    try {
      const { data } = await api.post("/auth/resend-otp", { challenge_id: challengeId });
      return {
        ok: true,
        challengeId: data.challenge_id,
        expiresAt: data.expires_at,
        resendAfter: data.resend_after_seconds || 30,
      };
    } catch (error) {
      return { ok: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      if (data.token) localStorage.setItem("wf_token", data.token);
      setUser(data);
      return { ok: true, user: data };
    } catch (error) {
      return { ok: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.warn("Logout request failed (clearing client state anyway):", error?.message);
    }
    localStorage.removeItem("wf_token");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, verifyOtp, resendOtp, register, logout, refreshUser: fetchMe }),
    [user, login, verifyOtp, resendOtp, register, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
